'use client';

import { useEffect, useRef, useState } from 'react';
import type { ProtoWallet } from '@ducat-unit/client-sdk';
import { useQuery } from '@tanstack/react-query';

import { getFrontendNetwork } from '@/config/deployment';
import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType } from '@/context/types';
import {
  getActiveWalletIdentity,
  getQuantaSignInIdentities,
  type QuantaHeaders,
  type QuantaSignInIdentities,
} from '@/hooks/api/quanta/auth';
import { readMigrationOwner, verifyMigrationOwner } from '@/hooks/api/quanta/migrationOwner';
import { resetQuantaQueries } from '@/hooks/api/quanta/quanta.helpers';
import { quantaApi } from '@/hooks/api/quanta/quantaApi';
import { useQuantaAuth } from '@/hooks/api/quanta/useQuantaAuth';
import {
  cancelMigration,
  confirmMigration,
  fetchMigrationAuthority,
  fetchMigrationCapabilities,
  fetchMigrationStatus,
  type MigrationAuthority,
  type MigrationRecovery,
  type MigrationStatus,
  type MigrationWallet,
  migrationErrorCode,
  readMigrationRecovery,
  requestMigrationChallenge,
  sameMigrationWallet,
  saveMigrationRecovery,
} from '@/hooks/api/quanta/walletMigration';
import { getWalletConnector, loadWalletConnector } from '@/lib/util';
import { useAppStore } from '@/stores/useAppStore';

import { type MigrationStep, QuantaMigrationContext } from './QuantaMigrationState';

interface SourceSession {
  authority: MigrationAuthority;
  headers: QuantaHeaders;
  provider: WalletType;
  wallet?: ProtoWallet;
  isolated?: boolean;
}

const signatureFrom = (result: { result?: { signature?: string } }) => {
  if (!result?.result?.signature) throw new Error('MIGRATION_SIGNATURE_REQUIRED');
  return result.result.signature;
};
const network = () => (['main', 'alpha-mainnet'].includes(getFrontendNetwork() ?? '') ? ('mainnet' as const) : ('testnet' as const));
const canonicalWallet = (
  identity: { address: string; publicKey: string },
  identityNetwork: MigrationWallet['network'],
): MigrationWallet => ({
  address: /^(bc1|tb1|bcrt1)/i.test(identity.address.trim()) ? identity.address.trim().toLowerCase() : identity.address.trim(),
  publicKey: (/^(bc1p|tb1p|bcrt1p)/i.test(identity.address) && identity.publicKey.length === 66
    ? identity.publicKey.slice(2)
    : identity.publicKey
  ).toLowerCase(),
  network: identityNetwork,
});

async function registeredSource(identities: QuantaSignInIdentities, assertCurrent: () => void) {
  // Preserve payment-first lookup: a shared vault alias must not hide a separate
  // legacy payment account whose points still need to be combined.
  for (const candidate of [identities.payment, identities.vault]) {
    if (!candidate) continue;
    let registered = false;
    try {
      const response = await quantaApi.get('/check', {
        headers: { 'x-address': candidate.address, 'x-user-public-key': candidate.publicKey },
      });
      registered = response.data.data.isRegistered === true;
    } catch (cause) {
      const response = (cause as { response?: { status?: number; data?: { data?: { isRegistered?: boolean } } } }).response;
      if (response?.status !== 404 || response.data?.data?.isRegistered !== false) throw cause;
    }
    assertCurrent();
    if (registered) return candidate;
  }
}

export function QuantaMigrationProvider({ children }: { children: React.ReactNode }) {
  const { wallet, walletType, accountRevision, client } = useDucatClient();
  const { signQuanta } = useQuantaAuth();
  const [step, setStep] = useState<MigrationStep>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedSourceAddresses, setCheckedSourceAddresses] = useState<string[]>([]);
  const [recovery, setRecovery] = useState<MigrationRecovery | null>(null);
  const source = useRef<SourceSession | null>(null);
  const [requiredOwner, setRequiredOwner] = useState<MigrationAuthority | null>(null);
  const reverseTarget = useRef<MigrationWallet | null>(null);
  const reverseTargetUserId = useRef<string | null>(null);
  // An authenticated previous owner may retain access to the canonical account
  // without migration authority. It can verify an existing connection, but
  // must never become a destination for a new account move or combination.
  const reverseAccountCheck = useRef<MigrationAuthority | null>(null);
  const [existingLink, setExistingLink] = useState<string | undefined>();
  const [alreadyLinked, setAlreadyLinked] = useState(false);
  const [addressesMatch, setAddressesMatch] = useState(false);
  const selectedSource = useRef<WalletType | null>(null);
  const [reverse, setReverse] = useState(false);
  const pending = useRef<MigrationRecovery | null>(null);
  const recoveryOwnerHeaders = useRef<QuantaHeaders | null>(null);
  const running = useRef(false);
  const epoch = useRef(0);
  const current = useRef({ wallet, walletType, accountRevision, mounted: true });
  current.current = { ...current.current, wallet, walletType, accountRevision };
  useEffect(() => {
    current.current.mounted = true;
    const saved = readMigrationRecovery();
    if (saved) {
      pending.current = saved;
      setRecovery(saved);
      setStep('recover');
    }
    return () => {
      current.current.mounted = false;
      epoch.current++;
    };
  }, []);
  const capabilities = useQuery({
    queryKey: ['quanta-wallet-migration-capabilities'],
    queryFn: fetchMigrationCapabilities,
    enabled: Boolean(wallet || recovery),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    retry: false,
  });
  const available = capabilities.data?.enabled === true && capabilities.data.proofVersion === 2;
  const canCombine = available && capabilities.data?.mergeProofVersion === 3;
  const canCrossNetwork = available && capabilities.data?.crossNetworkProofVersion === 4;
  const save = (value: MigrationRecovery | null) => {
    if (!value) recoveryOwnerHeaders.current = null;
    pending.current = value;
    setRecovery(value);
    saveMigrationRecovery(value);
  };
  const run = async (operation: (assertAlive: () => void) => Promise<void>) => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError(null);
    setCheckedSourceAddresses([]);
    const attempt = ++epoch.current;
    const assertAlive = () => {
      if (!current.current.mounted || epoch.current !== attempt) throw new Error('MIGRATION_WALLET_CHANGED');
    };
    try {
      await operation(assertAlive);
    } catch (cause) {
      if (current.current.mounted && epoch.current === attempt) {
        setError(
          migrationErrorCode(cause) ??
            (cause instanceof Error && cause.message.startsWith('MIGRATION_') ? cause.message : 'MIGRATION_FAILED'),
        );
        setStep(
          pending.current
            ? 'recover'
            : source.current
              ? 'connect'
              : requiredOwner
                ? 'verify-owner'
                : reverseTarget.current
                  ? 'select-source'
                  : 'idle',
        );
      }
    } finally {
      running.current = false;
      if (current.current.mounted) setBusy(false);
    }
  };
  const applyStatus = (result: MigrationStatus, saved: MigrationRecovery) => {
    if (result.status === 'completed') {
      if (result.userId !== saved.userId || !result.authority || !sameMigrationWallet(result.authority, saved.target)) {
        throw new Error('MIGRATION_RESULT_MISMATCH');
      }
      source.current = null;
      setRequiredOwner(null);
      save(null);
      setStep('completed');
      resetQuantaQueries();
    } else if (result.status === 'expired' || result.status === 'canceled') {
      source.current = null;
      setRequiredOwner(null);
      save(null);
      setStep('idle');
      setError(result.status === 'expired' ? 'MIGRATION_EXPIRED' : null);
    } else {
      setStep('recover');
      setError('MIGRATION_PENDING');
    }
  };
  const matchesCheckedAccount = (authority: MigrationAuthority) => {
    const expected = reverseAccountCheck.current;
    return (
      expected !== null &&
      authority.userId === expected.userId &&
      authority.revision === expected.revision &&
      authority.migrated === expected.migrated &&
      sameMigrationWallet(authority.authority, expected.authority)
    );
  };
  const begin = async () => {
    // connect() resolves after publishing React state, not after its consumers
    // render. Keep the handoff pending until this hook (including signQuanta's
    // provider-bound closure) sees the selected legacy wallet.
    if (step === 'authenticate-source' && (!wallet || walletType !== selectedSource.current)) return false;
    let prepared = false;
    await run(async (assertAlive) => {
      if (pending.current) throw new Error('MIGRATION_PENDING');
      if (!available) throw new Error('MIGRATION_UNAVAILABLE');
      const captured = current.current;
      const headers = useAppStore.getState().quantaHeaders;
      if (!captured.wallet || !captured.walletType || (!headers && !reverseTarget.current)) throw new Error('MIGRATION_LOGIN_REQUIRED');
      if (step === 'authenticate-source' && captured.walletType !== selectedSource.current) throw new Error('MIGRATION_SOURCE_CHANGED');
      setAddressesMatch(false);
      setAlreadyLinked(false);
      setStep('preparing');
      const assertSource = () => {
        assertAlive();
        if (
          current.current.wallet !== captured.wallet ||
          current.current.accountRevision !== captured.accountRevision ||
          useAppStore.getState().quantaHeaders !== headers
        )
          throw new Error('MIGRATION_WALLET_CHANGED');
      };
      if (captured.walletType === WalletType.Metamask) {
        if (!canCombine || !headers) throw new Error('MIGRATION_UNAVAILABLE');
        setReverse(true);
        selectedSource.current = null;
        const authority = await fetchMigrationAuthority(headers);
        assertSource();
        const identities = await getQuantaSignInIdentities('metamask', captured.wallet);
        assertSource();
        if (!identities) throw new Error('MIGRATION_TARGET_REQUIRED');
        // Older MetaMask accounts are owned by their payment identity, while
        // newer sign-ins may use a vault alias. Keep the actual account owner
        // as the merge target; that exact live key must approve the challenge.
        // canMigrate describes the current login proof, not the other live role.
        const owner = Object.values(identities).find(
          (identity) => identity && sameMigrationWallet(canonicalWallet(identity, network()), authority.authority),
        );
        reverseAccountCheck.current = null;
        let target = owner;
        if (!target) {
          // This signed endpoint already verified access to the account.
          // Previous owners retain login access after merges or migrations;
          // canMigrate/signingAuthority only govern account-control changes.
          const authenticated = canonicalWallet({ address: headers['x-address'], publicKey: headers['x-user-public-key'] }, network());
          target = Object.values(identities).find(
            (identity) =>
              identity &&
              sameMigrationWallet(canonicalWallet(identity, network()), authenticated) &&
              (!authority.signingAuthority || sameMigrationWallet(authenticated, authority.signingAuthority)),
          );
          if (!target) throw new Error('MIGRATION_AUTHORITY_REQUIRED');
          reverseAccountCheck.current = authority;
        }
        // A prior migration does not identify the legacy wallet selected next.
        // Check whether that specific source is already linked after selection.
        reverseTarget.current = canonicalWallet(target, network());
        reverseTargetUserId.current = authority.userId;
        setStep('select-source');
        prepared = true;
        return;
      }
      let sourceHeaders = headers;
      if (reverseTarget.current) {
        const identities = await getQuantaSignInIdentities(captured.walletType, captured.wallet);
        assertSource();
        if (!identities) throw new Error('MIGRATION_SOURCE_CHANGED');
        const existing = await registeredSource(identities, assertSource);
        if (!existing) {
          setCheckedSourceAddresses([
            ...new Set([identities.payment.address, identities.vault?.address].filter((address): address is string => !!address)),
          ]);
          throw new Error('MIGRATION_SOURCE_NOT_REGISTERED');
        }
        sourceHeaders = await signQuanta({ publish: false, identity: existing });
        assertSource();
      }
      if (!sourceHeaders) throw new Error('MIGRATION_LOGIN_REQUIRED');
      let authority = await fetchMigrationAuthority(sourceHeaders);
      assertSource();
      if (reverseAccountCheck.current) {
        // A fresh source login proves existing account access, even when Xverse
        // signs the nested form of the original native SegWit payment address.
        // This branch only acknowledges access: it cannot prepare a migration.
        const target = reverseTarget.current;
        if (!target || !matchesCheckedAccount(authority)) throw new Error('MIGRATION_AUTHORITY_REQUIRED');
        setAddressesMatch(
          sameMigrationWallet(
            canonicalWallet({ address: sourceHeaders['x-address'], publicKey: sourceHeaders['x-user-public-key'] }, network()),
            target,
          ),
        );
        setAlreadyLinked(true);
        setStep('completed');
        resetQuantaQueries();
        return;
      }
      if (
        reverseTarget.current &&
        authority.userId === reverseTargetUserId.current &&
        sameMigrationWallet(authority.authority, reverseTarget.current)
      ) {
        // The legacy signature proves access to an account already controlled
        // by the selected MetaMask wallet. There is no new move to approve.
        setAddressesMatch(
          sameMigrationWallet(
            canonicalWallet({ address: sourceHeaders['x-address'], publicKey: sourceHeaders['x-user-public-key'] }, network()),
            reverseTarget.current,
          ),
        );
        setAlreadyLinked(true);
        setStep('completed');
        resetQuantaQueries();
        return;
      }
      if (!authority.canMigrate) {
        // A vault login alias does not inherit account-management authority.
        // Ask the original owner key to authorize this explicit account move,
        // without replacing the user's ordinary Quanta login session.
        const identities = await getQuantaSignInIdentities(captured.walletType, captured.wallet);
        assertSource();
        const exactOwner =
          identities &&
          Object.values(identities).find(
            (identity) => identity && sameMigrationWallet(canonicalWallet(identity, network()), authority.authority),
          );
        const payment = identities?.payment;
        // This only chooses a candidate for a fresh signed server check. The
        // backend must authorize the immutable original owner before we proceed.
        const sameOriginalKey =
          !authority.migrated &&
          payment &&
          /^(02|03)[0-9a-f]{64}$/i.test(authority.authority.publicKey) &&
          payment.publicKey.toLowerCase() === authority.authority.publicKey.toLowerCase() &&
          authority.authority.network === network() &&
          ((/^(tb1q|bc1q)/i.test(authority.authority.address) && /^[23]/.test(payment.address)) ||
            (/^[23]/.test(authority.authority.address) && /^(tb1q|bc1q)/i.test(payment.address)));
        const owner = exactOwner ?? (sameOriginalKey ? payment : undefined);
        if (!owner) {
          if (reverseTarget.current && authority.migrated) {
            setExistingLink(authority.authority.address);
            setStep('linked-elsewhere');
            return;
          }
          if (authority.migrated) throw new Error('MIGRATION_AUTHORITY_REQUIRED');
          setRequiredOwner(authority);
          selectedSource.current = captured.walletType;
          setStep('verify-owner');
          return;
        }
        sourceHeaders = await signQuanta({ publish: false, identity: owner });
        assertSource();
        const authorized = await fetchMigrationAuthority(sourceHeaders);
        assertSource();
        if (
          !authorized.canMigrate ||
          authorized.userId !== authority.userId ||
          authorized.revision !== authority.revision ||
          !sameMigrationWallet(authorized.authority, authority.authority) ||
          !sameMigrationWallet(authorized.signingAuthority ?? authorized.authority, canonicalWallet(owner, network()))
        )
          throw new Error('MIGRATION_AUTHORITY_REQUIRED');
        authority = authorized;
      }
      source.current = { authority, headers: sourceHeaders, provider: captured.walletType, wallet: captured.wallet };
      setStep('connect');
      prepared = true;
    });
    return prepared;
  };
  const verifyOwner = (provider: WalletType) =>
    run(async (assertAlive) => {
      const expected = requiredOwner;
      const captured = current.current;
      if (!expected || pending.current) throw new Error('MIGRATION_AUTHORITY_REQUIRED');
      if (expected.authority.network !== network() && !canCrossNetwork) throw new Error('MIGRATION_UNAVAILABLE');
      // Connect MetaMask before the isolated proof. The legacy extension then has
      // no active app subscription that would reset it to the deployment's chain.
      if (!captured.wallet || captured.walletType !== WalletType.Metamask) throw new Error('MIGRATION_TARGET_REQUIRED');
      const assertCurrent = () => {
        assertAlive();
        if (
          current.current.wallet !== captured.wallet ||
          current.current.walletType !== captured.walletType ||
          current.current.accountRevision !== captured.accountRevision
        )
          throw new Error('MIGRATION_WALLET_CHANGED');
      };
      const target = await readTarget(captured.wallet);
      assertCurrent();
      if (!target) throw new Error('MIGRATION_TARGET_CHANGED');
      const proof = await verifyMigrationOwner(provider, expected, assertCurrent);
      const authorized = await fetchMigrationAuthority(proof.headers);
      assertCurrent();
      if (
        !authorized.canMigrate ||
        authorized.userId !== expected.userId ||
        authorized.revision !== expected.revision ||
        authorized.migrated !== expected.migrated ||
        !sameMigrationWallet(authorized.authority, expected.authority) ||
        !sameMigrationWallet(authorized.signingAuthority ?? authorized.authority, proof.identity)
      )
        throw new Error('MIGRATION_AUTHORITY_REQUIRED');
      const live = await readMigrationOwner(provider, authorized);
      assertCurrent();
      if (!sameMigrationWallet(live, proof.identity)) throw new Error('MIGRATION_SOURCE_CHANGED');
      source.current = { authority: authorized, headers: proof.headers, provider, isolated: true };
      setRequiredOwner(null);
      setStep('connect');
    });
  const connectSource = (provider: WalletType) =>
    run(async (assertAlive) => {
      if (!reverseTarget.current || ![WalletType.Xverse, WalletType.Unisat].includes(provider) || pending.current)
        throw new Error('MIGRATION_SOURCE_CHANGED');
      selectedSource.current = provider;
      setExistingLink(undefined);
      setAddressesMatch(false);
      const captured = current.current;
      const headers = useAppStore.getState().quantaHeaders;
      const target = reverseTarget.current;
      // A previous preparation attempt may already have connected the source.
      // Reconnecting that same account can be rejected as a duplicate by the
      // client. Re-read its live identity and continue without another switch.
      if (captured.wallet && captured.walletType === provider) {
        const identities = await getQuantaSignInIdentities(provider, captured.wallet);
        assertAlive();
        if (
          current.current.wallet !== captured.wallet ||
          current.current.walletType !== captured.walletType ||
          current.current.accountRevision !== captured.accountRevision
        )
          throw new Error('MIGRATION_WALLET_CHANGED');
        if (identities) {
          setStep('authenticate-source');
          return;
        }
      }
      // Reuse the authenticated MetaMask session for an identical live identity.
      // Reading the selected provider does not publish it as the app wallet.
      if (captured.walletType === WalletType.Metamask && captured.wallet && headers) {
        const assertUnchanged = () => {
          assertAlive();
          if (
            current.current.wallet !== captured.wallet ||
            current.current.walletType !== captured.walletType ||
            current.current.accountRevision !== captured.accountRevision ||
            useAppStore.getState().quantaHeaders !== headers
          )
            throw new Error('MIGRATION_WALLET_CHANGED');
        };
        const identities = await getQuantaSignInIdentities(provider, captured.wallet);
        assertUnchanged();
        if (
          identities &&
          Object.values(identities).some((identity) => identity && sameMigrationWallet(canonicalWallet(identity, network()), target))
        ) {
          const existing = await registeredSource(identities, assertUnchanged);
          if (existing && sameMigrationWallet(canonicalWallet(existing, network()), target)) {
            const authority = await fetchMigrationAuthority(headers);
            assertUnchanged();
            if (
              reverseAccountCheck.current
                ? !matchesCheckedAccount(authority)
                : authority.userId !== reverseTargetUserId.current || !sameMigrationWallet(authority.authority, target)
            )
              throw new Error('MIGRATION_TARGET_CHANGED');
            // A source account switch while the server lookup is pending must
            // not turn into a misleading success message.
            const live = await getQuantaSignInIdentities(provider, captured.wallet);
            assertUnchanged();
            if (
              !live ||
              !(['payment', 'vault'] as const).every((role) => {
                const before = identities[role];
                const after = live[role];
                return before && after
                  ? sameMigrationWallet(canonicalWallet(before, network()), canonicalWallet(after, network()))
                  : before === after;
              })
            )
              throw new Error('MIGRATION_SOURCE_CHANGED');
            setAddressesMatch(true);
            setStep('completed');
            return;
          }
        }
      }
      if (!(await client.connect(provider))) throw new Error('MIGRATION_CONNECTION_FAILED');
      assertAlive();
      setStep('authenticate-source');
    });
  const readTarget = async (targetWallet: ProtoWallet) => {
    if (!reverseTarget.current) {
      const identity = await getActiveWalletIdentity('metamask', targetWallet, 'vault');
      return identity ? canonicalWallet(identity, network()) : null;
    }
    // Revalidate both roles against the current Snap account after every switch
    // and signature. Never fall back to another address if the saved owner moved.
    const identities = await getQuantaSignInIdentities('metamask', targetWallet);
    if (!identities) return null;
    return (
      Object.values(identities)
        .filter((identity) => identity !== null)
        .map((identity) => canonicalWallet(identity, network()))
        .find((identity) => reverseTarget.current && sameMigrationWallet(identity, reverseTarget.current)) ?? null
    );
  };
  const approve = () =>
    run(async (assertAlive) => {
      if (pending.current) throw new Error('MIGRATION_PENDING');
      if (reverseAccountCheck.current) throw new Error('MIGRATION_AUTHORITY_REQUIRED');
      const from = source.current;
      const captured = current.current;
      if (!available || !from || !captured.wallet || captured.walletType !== WalletType.Metamask)
        throw new Error('MIGRATION_TARGET_REQUIRED');
      const assertTarget = () => {
        assertAlive();
        if (
          current.current.wallet !== captured.wallet ||
          current.current.walletType !== WalletType.Metamask ||
          current.current.accountRevision !== captured.accountRevision
        )
          throw new Error('MIGRATION_WALLET_CHANGED');
      };
      const target = await readTarget(captured.wallet);
      assertTarget();
      if (!target) throw new Error(reverseTarget.current ? 'MIGRATION_TARGET_CHANGED' : 'MIGRATION_TARGET_REQUIRED');
      const signingAuthority = from.authority.signingAuthority ?? from.authority.authority;
      if (
        (target.network !== signingAuthority.network && !canCrossNetwork) ||
        sameMigrationWallet(target, signingAuthority) ||
        target.publicKey.slice(-64) === signingAuthority.publicKey.slice(-64)
      )
        throw new Error('MIGRATION_TARGET_INVALID');
      if (reverseTarget.current && !sameMigrationWallet(target, reverseTarget.current)) throw new Error('MIGRATION_TARGET_CHANGED');
      const challenge = await requestMigrationChallenge(
        from.authority,
        target,
        from.headers,
        window.location.origin,
        canCombine,
        ...(target.network !== signingAuthority.network ? [canCrossNetwork] : []),
      );
      assertTarget();
      if (challenge.proofVersion === 3 && !challenge.context.targetUserId) throw new Error('MIGRATION_RESULT_MISMATCH');
      const combinedTargetUserId =
        challenge.proofVersion === 3 || challenge.proofVersion === 4 ? challenge.context.targetUserId : undefined;
      if (reverseTargetUserId.current && (combinedTargetUserId ?? from.authority.userId) !== reverseTargetUserId.current)
        throw new Error('MIGRATION_TARGET_CHANGED');
      const saved = {
        nonce: challenge.nonce,
        userId: combinedTargetUserId ?? from.authority.userId,
        source: signingAuthority,
        target,
        expiresAt: challenge.expiresAt,
      };
      save(saved);
      const assertSourceIdentity = async () => {
        // Selecting a provider here is intentional: the old wallet remains the source signer,
        // even though the app's active wallet is now MetaMask.
        await loadWalletConnector(from.provider);
        assertTarget();
        getWalletConnector(from.provider);
        if (from.isolated) {
          const live = await readMigrationOwner(from.provider, from.authority);
          assertTarget();
          if (!sameMigrationWallet(live, signingAuthority)) throw new Error('MIGRATION_SOURCE_CHANGED');
          return;
        }
        const live = await getQuantaSignInIdentities(from.provider, from.wallet);
        assertTarget();
        if (
          !live ||
          !Object.values(live).some((identity) => identity && sameMigrationWallet(canonicalWallet(identity, network()), signingAuthority))
        ) {
          throw new Error('MIGRATION_SOURCE_CHANGED');
        }
      };
      setStep('approving-source');
      await assertSourceIdentity();
      const sourceSignature = signatureFrom(
        await getWalletConnector(from.provider).signMessage({
          address: signingAuthority.address,
          message: challenge.message,
        }),
      );
      await assertSourceIdentity();
      setStep('approving-target');
      const targetBefore = await readTarget(captured.wallet);
      assertTarget();
      if (!targetBefore || !sameMigrationWallet(targetBefore, target)) throw new Error('MIGRATION_WALLET_CHANGED');
      const targetSignature = signatureFrom(await client.signMessage({ address: target.address, message: challenge.message }));
      const targetAfter = await readTarget(captured.wallet);
      assertTarget();
      if (!targetAfter || !sameMigrationWallet(targetAfter, target)) throw new Error('MIGRATION_WALLET_CHANGED');
      await assertSourceIdentity();
      setStep('submitting');
      const result = await confirmMigration(challenge, sourceSignature, targetSignature, from.headers);
      // A response after a switch cannot publish account data. The saved handle enables recovery.
      assertTarget();
      applyStatus(result, saved);
    });
  const recoveryHeaders = async (assertAlive: () => void) => {
    if (source.current) return source.current.headers;
    if (recoveryOwnerHeaders.current) return recoveryOwnerHeaders.current;
    const captured = current.current;
    const saved = pending.current;
    if (!captured.wallet || !captured.walletType || !saved) throw new Error('MIGRATION_LOGIN_REQUIRED');
    // Older v2 approvals may bind MetaMask's payment address. Recover with the
    // exact saved identity, even though new Quanta sign-ins use its vault key.
    const identities = await getQuantaSignInIdentities(captured.walletType, captured.wallet);
    assertAlive();
    if (current.current.wallet !== captured.wallet || current.current.accountRevision !== captured.accountRevision)
      throw new Error('MIGRATION_WALLET_CHANGED');
    const signer =
      identities &&
      Object.values(identities).find((candidate) => {
        if (!candidate) return false;
        const identity = canonicalWallet(candidate, network());
        return sameMigrationWallet(identity, saved.source) || sameMigrationWallet(identity, saved.target);
      });
    if (!signer) throw new Error('MIGRATION_RECOVERY_WALLET_REQUIRED');
    const headers = await signQuanta({ publish: false, identity: signer });
    assertAlive();
    if (current.current.wallet !== captured.wallet || current.current.accountRevision !== captured.accountRevision)
      throw new Error('MIGRATION_WALLET_CHANGED');
    const identity = canonicalWallet({ address: headers['x-address'], publicKey: headers['x-user-public-key'] }, network());
    if (!sameMigrationWallet(identity, saved.source) && !sameMigrationWallet(identity, saved.target))
      throw new Error('MIGRATION_RECOVERY_WALLET_REQUIRED');
    return headers;
  };
  const recover = () =>
    run(async (assertAlive) => {
      const saved = pending.current;
      if (!saved) return;
      const captured = current.current;
      const headers = await recoveryHeaders(assertAlive);
      const result = await fetchMigrationStatus(saved.nonce, headers);
      assertAlive();
      if (current.current.wallet !== captured.wallet || current.current.accountRevision !== captured.accountRevision)
        throw new Error('MIGRATION_WALLET_CHANGED');
      applyStatus(result, saved);
    });
  const recoverWithOwner = (provider: WalletType) =>
    run(async (assertAlive) => {
      const saved = pending.current;
      const captured = current.current;
      if (!saved) return;
      if (!captured.wallet || captured.walletType !== WalletType.Metamask) throw new Error('MIGRATION_TARGET_REQUIRED');
      const assertCurrent = () => {
        assertAlive();
        if (
          current.current.wallet !== captured.wallet ||
          current.current.walletType !== captured.walletType ||
          current.current.accountRevision !== captured.accountRevision
        )
          throw new Error('MIGRATION_WALLET_CHANGED');
      };
      // Stored metadata grants no authority. The signed status endpoint checks
      // account access; cancellation also requires the exact original signer.
      const proof = await verifyMigrationOwner(
        provider,
        {
          authority: saved.source,
          signingAuthority: saved.source,
          userId: saved.userId,
          revision: 0,
          migrated: true,
          canMigrate: false,
        },
        assertCurrent,
      );
      const result = await fetchMigrationStatus(saved.nonce, proof.headers);
      assertCurrent();
      recoveryOwnerHeaders.current = proof.headers;
      applyStatus(result, saved);
    });
  const cancel = () =>
    run(async (assertAlive) => {
      const saved = pending.current;
      if (saved) {
        const captured = current.current;
        const headers = await recoveryHeaders(assertAlive);
        const result = await cancelMigration(saved.nonce, headers);
        assertAlive();
        if (current.current.wallet !== captured.wallet || current.current.accountRevision !== captured.accountRevision)
          throw new Error('MIGRATION_WALLET_CHANGED');
        applyStatus(result, saved);
      } else {
        source.current = null;
        setRequiredOwner(null);
        reverseTarget.current = null;
        reverseTargetUserId.current = null;
        reverseAccountCheck.current = null;
        selectedSource.current = null;
        setExistingLink(undefined);
        setAlreadyLinked(false);
        setAddressesMatch(false);
        setReverse(false);
        setStep('idle');
      }
    });
  const dismiss = () => {
    if (!running.current && !pending.current) {
      source.current = null;
      setRequiredOwner(null);
      reverseTarget.current = null;
      reverseTargetUserId.current = null;
      reverseAccountCheck.current = null;
      selectedSource.current = null;
      setExistingLink(undefined);
      setAlreadyLinked(false);
      setAddressesMatch(false);
      setReverse(false);
      setStep('idle');
      setError(null);
    }
  };
  const active = step !== 'idle' && step !== 'completed';
  return (
    <QuantaMigrationContext.Provider
      value={{
        active,
        available,
        canCombine,
        reverse,
        connectSource,
        availabilityLoading: capabilities.isLoading,
        step,
        busy,
        error,
        checkedSourceAddresses,
        recovery,
        requiredOwner: requiredOwner?.authority,
        verifyOwner,
        recoverWithOwner,
        existingLink,
        alreadyLinked,
        addressesMatch,
        chooseAnotherSource: () => {
          if (running.current || pending.current || !reverseTarget.current) return;
          source.current = null;
          setRequiredOwner(null);
          setExistingLink(undefined);
          setError(null);
          setStep('select-source');
        },
        sourceProvider: source.current?.provider ?? selectedSource.current ?? undefined,
        sourceReady: Boolean(wallet && walletType === selectedSource.current),
        sourceAddress:
          (source.current?.authority.signingAuthority ?? source.current?.authority.authority)?.address ?? recovery?.source.address,
        targetAddress: recovery?.target.address ?? reverseTarget.current?.address,
        begin,
        approve,
        recover,
        cancel,
        dismiss,
        retryAvailability: () => {
          void capabilities.refetch();
        },
      }}
    >
      {children}
    </QuantaMigrationContext.Provider>
  );
}
